/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderPermission,
  ContactDeliveryMethod,
  JobVariationStatus,
} from '@gh/builder-shared-data';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getInvalidFields,
  getRoutePath,
  IS_REGION_AU_OR_NZ,
  isEntityInvalid,
  isRouteForCreationMode,
  markFormStateAsTouched,
  PlainImmutablePageState,
  queryRecalculationNeeded,
  resetDirtyType,
  setEntityFields,
  setFormStateSnapshotValue,
  UserContext,
} from '@gh/common-shared';
import { AutocompleteItem, ID } from '@gh/common-shared-data';
import { FormMode, isEditableFormMode } from '@gh/core-ui';
import { isAllTrue, isNotNil } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { map$ } from '@gh/rx/operators';
import { constant, find, first, isEmpty, isNil, negate, pick } from 'lodash';
import {
  getJobCoElementId,
  isJobCoImmediatelyClaimed,
  JOB_CO_DETAILS_INIT_STATE,
  JobCoDetails,
  JobCoDetailsBottomInfo,
  JobCoDetailsMasterInfo,
  JobCoDetailsProduceStatus,
  JobCoElement,
  jobCoElementArr,
  queryJobCoInProducing,
  setJobCoElementField,
  validateJobCoBottomInfo,
  validateJobCoMasterInfo,
} from '../../resources/job-co-details.model';

@Injectable()
export class JobCoDetailsFormState extends PlainImmutablePageState<JobCoDetails> {
  recalculationNeeded$ = this.select(queryRecalculationNeeded);
  masterInfo$ = this.select(({ masterInfo }) => masterInfo);
  canBeProduced$ = this.masterInfo$.pipe(
    map$(getFormStateSnapshotValue),
    map$(({ producedDate, status }) => status === JobVariationStatus.Quote && isNil(producedDate)));
  drawRuleStages$ = this.select(({ drawRuleStages }) => drawRuleStages);
  elements$ = this.select(({ elements }) => elements);
  derivedInfo$ = this.select(({ derivedInfo }) => derivedInfo);
  hasChanges$ = this.elements$.pipe(map$(negate(isEmpty)));
  focusedCell$ = this.select(({ focusedCell }) => focusedCell);
  status$ = this.select(({ masterInfo }) => getFormStateSnapshotValue(masterInfo).status);
  producedDate$ = this.select(({ masterInfo }) => getFormStateSnapshotValue(masterInfo).producedDate);
  isPrintOnProcess$ = this.select(({ isPrintOnProcess }) => isPrintOnProcess);
  hasPriceBook$ = this.masterInfo$.pipe(
    map$(getFormStateSnapshotValue),
    map$((masterInfo) => isNotNil(masterInfo.priceBook)));
  bottomInfo$ = this.select(({ bottomInfo }) => bottomInfo);
  canPrint$ = this.select(({ masterInfo: snapshot }) => {
    const masterInfo = getFormStateSnapshotValue(snapshot);
    return isNotNil(masterInfo.producedDate)
      && !(<JobVariationStatus[]>[JobVariationStatus.Reversed, JobVariationStatus.Rejected])
        .includes(masterInfo.status);
  });
  elementsEditable$ = combineLatest$(
    this.mode$.pipe(map$(isEditableFormMode)),
    this.select(({ masterInfo: snapshot }) => {
      const masterInfo = getFormStateSnapshotValue(snapshot);
      return masterInfo.status === JobVariationStatus.Quote && isNil(masterInfo.producedDate);
    })).pipe(
    map$(isAllTrue));
  produceStatus$ = this.select(({ produceStatus }) => produceStatus);
  producing$ = this.select(queryJobCoInProducing);

  constructor(private route: ActivatedRoute,
              private userContext: UserContext) {
    super(JOB_CO_DETAILS_INIT_STATE);
  }

  get jobNumber(): string {
    return this.value.jobNumber;
  }

  get customerName(): string {
    return this.value.customerName;
  }

  get siteAddress(): string {
    return this.value.siteAddress;
  }

  get variationNumber(): number {
    return this.value.variationNumber;
  }

  get addedDate(): Date {
    return this.value.addedDate;
  }

  get description(): string {
    const masterInfo = getFormStateSnapshotValue(this.value.masterInfo);
    return masterInfo.description;
  }

  get status(): JobVariationStatus {
    const masterInfo = getFormStateSnapshotValue(this.value.masterInfo);
    return masterInfo.status;
  }

  get isApproved(): boolean {
    return this.status === JobVariationStatus.Approved;
  }

  get isClaimed(): boolean {
    const masterInfo = getFormStateSnapshotValue(this.value.masterInfo);
    return masterInfo.isClaimed;
  }

  get isImmediatelyClaimed(): boolean {
    return isJobCoImmediatelyClaimed(this.value);
  }

  get jobId(): ID {
    return this.value.jobId;
  }

  get customerId(): ID {
    return this.value.customerId;
  }

  get customerEmail(): ID {
    return this.value.customerEmail;
  }

  get changeOrderId(): ID {
    return this.value.variationId;
  }

  get masterInfo(): FormStateSnapshot<JobCoDetailsMasterInfo> {
    return this.value.masterInfo;
  }

  get priceBook(): Maybe<AutocompleteItem> {
    const masterInfo = getFormStateSnapshotValue(this.value.masterInfo);
    return masterInfo.priceBook;
  }

  get deliveryBy(): ContactDeliveryMethod {
    return this.value.deliveryBy;
  }

  get hasOrdersToEmail(): boolean {
    const { ordersCountPrint, ordersCount } = this.value;
    return (ordersCount - ordersCountPrint) > 0;
  }

  get producingInProcess(): boolean {
    return this.project(queryJobCoInProducing);
  }

  get hasChanges(): boolean {
    return !isEmpty(this.value.elements);
  }

  get invalid(): boolean {
    return this.isMasterInfoInvalid || this.isElementsInvalid || this.isBottomInfoInvalid;
  }

  get isMasterInfoInvalid(): boolean {
    return isNotNil(validateJobCoMasterInfo(getFormStateSnapshotValue(this.value.masterInfo)));
  }

  get isElementsInvalid(): boolean {
    return isNotNil(this.firstInvalidElement);
  }

  get isBottomInfoInvalid(): boolean {
    return IS_REGION_AU_OR_NZ
      ? isNotNil(validateJobCoBottomInfo(getFormStateSnapshotValue(this.value.bottomInfo)))
      : false;
  }

  get firstInvalidElement(): Maybe<JobCoElement> {
    return find<JobCoElement>(this.value.elements, isEntityInvalid);
  }

  build(details: JobCoDetails): void {
    this.setValue(details);
    this.updateMode();
  }

  mergeOnProcess(details: JobCoDetails): void {
    this.mergeOnRecalculate(details);
    this.setFields({
      variationId: details.variationId,
      masterInfo: details.masterInfo,
      bottomInfo: details.bottomInfo,
      elements: jobCoElementArr.updateAll(this.value.elements, resetDirtyType),
      version: details.version,
      produceStatus: details.produceStatus,
      isPrintOnProcess: true,
      deliveryBy: details.deliveryBy,
    });
    this.updateMode();
    this.resetDirty();
  }

  mergeOnRecalculate(details: JobCoDetails): void {
    this.setField('isRecalculationNeeded', false);
    this.setField('derivedInfo', details.derivedInfo);
    this.setField('bottomInfo', details.bottomInfo);
    this.setField('elements', jobCoElementArr.mergeWith(
      this.value.elements,
      details.elements,
      (oldElement, newElement) =>
        setEntityFields(oldElement, pick(newElement, ['priceExtension', 'priceWithGst', 'orderQuantity', 'price', 'memo']))));    
    this.setField('deliveryBy', details.deliveryBy);
  }

  setProduceStatus(produceStatus: JobCoDetailsProduceStatus): void {
    this.setField('produceStatus', produceStatus);
    this.updateMode();
  }

  setMasterInfo(snapshot: FormStateSnapshot<JobCoDetailsMasterInfo>): void {
    this.setField('masterInfo', snapshot);
    this.setDirty();
  }

  setBottomInfo(snapshot: FormStateSnapshot<JobCoDetailsBottomInfo>): void {
    this.setField('bottomInfo', snapshot);
    this.setDirty();
    this.setRecalculationNeeded();
  }

  setElementField(element: JobCoElement, field: string, value: any): void {
    this.startChangeDetection();
    this.setField('elements', jobCoElementArr.merge(
      this.value.elements,
      getJobCoElementId(element),
      (el) => setJobCoElementField(el, field, value)));
    if (this.changed && field === 'quantity') {
      this.setRecalculationNeeded();
    }
    this.setDirtyIfChanged();
  }

  moveElementCreditDebit(element: JobCoElement): void {
    this.updateElementsAndMarkAsDirty((elements) => jobCoElementArr.merge(
      elements,
      getJobCoElementId(element),
      (el) => setJobCoElementField(el, 'isCredit', !el.isCredit)));
    this.setRecalculationNeeded();
  }

  dragRow(updatedElements: JobCoElement[]): void {
    this.updateElementsAndMarkAsDirty(constant([]));
    this.updateElementsAndMarkAsDirty((elements) => jobCoElementArr.append(updatedElements));
    this.setDirty();
  }

  addElements(newElements: JobCoElement[]): void {
    this.updateElementsAndMarkAsDirty((elements) => jobCoElementArr.prepend(elements, ...newElements));
    this.setRecalculationNeeded();
  }

  removeElement(element: JobCoElement): void {
    this.updateElementsAndMarkAsDirty((elements) => jobCoElementArr.remove(elements, getJobCoElementId(element)));
    this.setRecalculationNeeded();
  }

  removeAllElements(): void {
    this.updateElementsAndMarkAsDirty(constant([]));
    this.setRecalculationNeeded();
  }

  setPrintOnProcess(isPrintOnProcess: boolean): void {
    this.setField('isPrintOnProcess', isPrintOnProcess);
  }

  resetFocusedCell(): void {
    this.setField('focusedCell', void 0);
  }

  highlightMasterInfo(): void {
    this.setField('masterInfo', markFormStateAsTouched(this.value.masterInfo));
  }

  highlightBottomInfo(): void {
    this.setField('bottomInfo', markFormStateAsTouched(this.value.bottomInfo));
  }

  goToInvalidElementField(invalidElement?: JobCoElement): void {
    const invalidField = invalidElement ? first(getInvalidFields(invalidElement)) : void 0;
    if (invalidElement && invalidField) {
      this.setField('focusedCell', { id: getJobCoElementId(invalidElement), field: invalidField });
    }
  }

  approve(): void {
    this.setField('masterInfo', setFormStateSnapshotValue(
      this.value.masterInfo,
      (state) => setEntityFields(state, {
        status: JobVariationStatus.Approved,
        isStatusChanged: true,
      })));
    this.setDirty();
  }

  reject(): void {
    this.setField('masterInfo', setFormStateSnapshotValue(
      this.value.masterInfo,
      (state) => setEntityFields(state, {
        status: JobVariationStatus.Rejected,
        isStatusChanged: true,
      })));
    this.setDirty();
  }

  reverse(): void {
    this.setField('masterInfo', setFormStateSnapshotValue(
      this.value.masterInfo,
      (state) => setEntityFields(state, {
        status: JobVariationStatus.Reversed,
        isStatusChanged: true,
      })));
    this.setDirty();
  }

  changeRound(): void {
    this.setRecalculationNeeded();
  }

  private updateElementsAndMarkAsDirty(transform: IdentityFn<JobCoElement[]>): void {
    this.startChangeDetection();
    this.setField('elements', transform(this.value.elements));
    this.setDirtyIfChanged();
  }

  private setRecalculationNeeded(): void {
    this.setField('isRecalculationNeeded', true);
  }

  private updateMode(): void {
    if (isRouteForCreationMode(this.route)) {
      this.setMode(FormMode.CreateMode);
    } else if (this.userContext.hasPermission(BuilderPermission.JobAdmin.JobChangeOrders.EDIT)
      && (
        !this.value.isFinalized
        && !this.value.isNotProceeding
        && !this.project(queryJobCoInProducing)
        && !/\/invoice$/.test(getRoutePath(this.route))
      )) {
      this.setMode(FormMode.EditMode);
    } else {
      this.setMode(FormMode.ViewMode);
    }
  }
}
